// Toile sign-in card — thinking/design/toile_surfaces/SignIn.dc.html
//
// Card: 420px, surface-grey ground, hairline border, radius 16, padding 34/32/28.
// Every colour, radius and face below is a --tl-* token. The narcis original this
// is ported from (products/narcis/components/SignInCard/SignInCard.module.scss) keeps the same
// class names and the same state machine; only the tokens and three measurements differ.

.card {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 420px;
    max-width: calc(100vw - 32px);
    background: var(--tl-surface);
    border: 1px solid var(--tl-border);
    border-radius: var(--tl-radius-container);
    padding: 34px 32px 28px;
    font-family: var(--tl-font);

    animation: cardMount 240ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

@keyframes cardMount {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// ── Stacked lockup ────────────────────────────────────────────────────────────

.lockup {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin: 0 0 22px;
}

.word {
    font-weight: 500;
    font-size: 15px;
    letter-spacing: -0.02em;
    line-height: 1;
    color: var(--tl-text);
    text-transform: lowercase;
}

// ── Title and subtitle ────────────────────────────────────────────────────────

.title {
    font-weight: 500;
    font-size: 22px;
    letter-spacing: -0.01em;
    color: var(--tl-text);
    margin: 0 0 4px;
    text-align: center;
}

.subtitle {
    font-size: 13.5px;
    font-weight: 400;
    color: var(--tl-muted);
    margin: 0 0 22px;
    text-align: center;
}

// ── Error banner ──────────────────────────────────────────────────────────────
// Surface-grey box, hue on the dot and the text only — never a red fill (Errors.dc.html).

.errorBanner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
    padding: 12px 14px;
    border-radius: var(--tl-radius-chrome);
    background: var(--tl-stage);
    border: 1px solid var(--tl-border-strong);
    margin-bottom: 16px;
    font-size: 13px;
    text-align: left;
}

.errorDot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--tl-error);
    margin-top: 6px;
    flex: none;
}

.errorText {
    flex: 1;
    color: var(--tl-error);
    line-height: 1.5;
}

.errorBannerDismiss {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--tl-dim);
    font-size: 16px;
    line-height: 1;
    padding: 0;
    flex-shrink: 0;

    &:hover {
        color: var(--tl-muted);
    }
}

// ── Google button — ghost on the stage, never a white pill ────────────────────

.googleForm {
    width: 100%;

    // A second provider button (Facebook) under Google's.
    & + & {
        margin-top: 10px;
    }
}

.googleBtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 11px 16px;
    background: var(--tl-stage);
    color: var(--tl-text);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-chrome);
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: border-color 150ms ease, background 150ms ease;

    &:hover {
        border-color: var(--tl-muted);
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--tl-accent) 18%, transparent);
    }
}

// ── Spinner (Google in-flight) ────────────────────────────────────────────────

.spinner {
    animation: spin 700ms linear infinite;
    color: var(--tl-accent);
    flex-shrink: 0;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

// ── "or" divider ──────────────────────────────────────────────────────────────

.divider {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    margin: 16px 0;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--tl-border);
    }

    span {
        font-family: var(--tl-font-label);
        font-size: 10px;
        color: var(--tl-dim);
        letter-spacing: 0.12em;
        text-transform: uppercase;
    }
}

// ── Magic-link row ────────────────────────────────────────────────────────────

.magicRow {
    display: flex;
    align-items: stretch;
    gap: 8px;
    width: 100%;
}

.emailInput {
    flex: 1;
    min-width: 0;
    background: var(--tl-stage);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-chrome);
    padding: 10px 13px;
    color: var(--tl-text);
    font-family: inherit;
    font-size: 14px;
    outline: none;
    transition: border-color 150ms ease, box-shadow 150ms ease;

    &::placeholder {
        color: var(--tl-dim);
    }

    &:focus {
        border-color: var(--tl-accent);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--tl-accent) 18%, transparent);
    }
}

.emailInputError {
    border-color: var(--tl-error);

    &:focus {
        border-color: var(--tl-error);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--tl-error) 16%, transparent);
    }
}

// Tungsten is the page's one solid CTA and it is lit — the glow IS the affordance, so hover
// intensifies it rather than shifting the hue (identity sheet; PackHeroStage.module.scss:202).
.magicBtn {
    padding: 10px 18px;
    background: var(--tl-accent);
    color: var(--tl-accent-ink);
    border: none;
    border-radius: var(--tl-radius-chrome);
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 0 0 24px color-mix(in srgb, var(--tl-accent) 35%, transparent);
    transition: box-shadow 150ms ease, opacity 150ms ease;

    &:hover:not(:disabled) {
        box-shadow: 0 0 30px color-mix(in srgb, var(--tl-accent) 55%, transparent);
    }

    &:disabled {
        opacity: 0.6;
        box-shadow: none;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px var(--tl-surface), 0 0 0 5px var(--tl-accent);
    }
}

// ── Field-level error ─────────────────────────────────────────────────────────

.fieldError {
    font-size: 12px;
    color: var(--tl-error);
    margin: 8px 0 0;
    width: 100%;
    text-align: left;
}

// ── Sent (success) state ──────────────────────────────────────────────────────

.sentBlock {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    background: var(--tl-stage);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-chrome);
    padding: 14px 16px;
    animation: fadeIn 180ms ease both;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.sentCheck {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1px solid var(--tl-accent);
    color: var(--tl-accent);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    box-shadow: 0 0 14px color-mix(in srgb, var(--tl-accent) 30%, transparent);
    flex: none;
}

.sentHeading {
    display: block;
    font-weight: 500;
    font-size: 14px;
    color: var(--tl-text);
}

.sentBody {
    font-size: 12.5px;
    color: var(--tl-muted);
}

.retryLink {
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 400;
    color: var(--tl-dim);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin: 12px 0 0;

    &:hover {
        color: var(--tl-muted);
    }
}

// ── Consent line + back link ──────────────────────────────────────────────────

.tos {
    font-size: 11.5px;
    font-weight: 400;
    color: var(--tl-dim);
    margin: 18px 0 0;
    text-align: center;

    a {
        color: var(--tl-dim);
        text-decoration: underline;

        &:hover {
            color: var(--tl-muted);
        }
    }
}

.backLink {
    font-family: inherit;
    font-size: 13px;
    font-weight: 400;
    color: var(--tl-dim);
    text-decoration: none;
    margin-top: 14px;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        color: var(--tl-muted);
    }
}
